import React from 'react';
import contensis from 'Common/api/contensis';
import { Op, OrderBy } from 'contensis-delivery-api';
import cmsParser from 'Common/util/cmsParser';
import setCacheHeaders from 'Common/func/setCacheHeaders';
import { excludeMetadata } from 'Common/util/contensis/api';

import Metadata from 'Components/Metadata';
import MainLayout from 'Layout/Main';
import Template, { datamap, PageData } from 'Templates/SolentStories/Story';

import type { NextPageWithLayout, GetServerSideProps } from 'next';

const Story: NextPageWithLayout<PageData> = ({ entry, relatedCourses, moreStories, ancestors }) => {
    return (
        <>
            <Metadata {...cmsParser.metadata(entry)} />
            <Template
                {...datamap({
                    entry,
                    relatedCourses,
                    moreStories,
                    ancestors
                })}
            />
        </>
    );
};

Story.getLayout = (page, props) => {
    return <MainLayout {...props}>{page}</MainLayout>;
};

export const getServerSideProps: GetServerSideProps<any> = async ({ params, preview, res }) => {
    const { fetch, surrogateKeys } = contensis({ preview, fetchSurrogateKeys: true });
    const { story } = params as { story: string };
    const { data, error } = await fetch(async (client, search) => {
        const resPage = await client.nodes.get({
            path: `/graduate-stories/${story}`,
            entryLinkDepth: 2,
            entryFields: ['*', '-relatedCourseCards', ...excludeMetadata]
        });

        const resRelatedCourses = await client.nodes.get({
            path: `/graduate-stories/${story}`,
            entryLinkDepth: 1,
            entryFields: [
                'relatedCourseCards',
                'courseCardImage',
                'degreeType',
                'name',
                'courseDescription'
            ]
        });

        const storiesQuery = [
            Op.equalTo('sys.contentTypeId', resPage.entry.sys.contentTypeId),
            Op.in('subject', ...[...new Set([resPage.entry.subject.key])]),
            Op.not(Op.equalTo('sys.id', resPage.entry.sys.id)),
            Op.exists('sys.uri', true)
        ];

        const resStories = await search(storiesQuery, {
            linkDepth: 3,
            pageSize: 4,
            orderBy: OrderBy.desc('sys.version.created'),
            fields: [
                'id',
                'sys',
                'cardImage',
                'listingCardImageDefault',
                'listingCardImageOnHover',
                'student',
                'person',
                'currentRole',
                'company',
                'courseCardQuote',
                'name',
                'position',
                'subject',
                'forename',
                'surname',
                ...excludeMetadata
            ]
        });

        const moreStories = resStories.items;

        if (!resPage.entry) {
            return { error: true };
        }

        const ancestors = await client.nodes.getAncestors({
            id: resPage.id,
            entryFields: ['title']
        });

        return {
            entry: { ...resPage.entry },
            relatedCourses: resRelatedCourses.entry.relatedCourseCards,
            moreStories,
            ancestors
        };
    });

    if (error) {
        return { notFound: true };
    } else {
        setCacheHeaders(res, surrogateKeys);

        return { props: data };
    }
};

export default Story;
